import { useFetchLoadTendersQuery } from '@api/Trinium/Endpoints/EDI/slice';
import { DataGridPro, GRID_CHECKBOX_SELECTION_COL_DEF, GridSortModel } from '@mui/x-data-grid-pro';
import { ediLoadTenderColumns } from './types';
import EdiRowSelectCheckbox from './Cells/EdiRowSelectCheckbox';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import BrandLinearProgress from '@/Infrastructure/Components/Loading/BrandLinearProgress';
import { GenericOverlay } from '../GenericNoRowsOverlay';
import EdiGridToolbar from './EdiGridToolbar';
import { useState } from 'react';
import { EdiStatus } from '@api/Trinium/Endpoints/EDI/types';
import { applyThemeGridSxOverrides } from '@/Infrastructure/Theme/AppThemeOverrides';
import { emptyRowSet } from '../types';
import { Theme, useMediaQuery } from '@mui/material';
import EdiLoadTendersContextProvider from './EdiLoadTenderContext';
import { dayjsNow } from '@/Infrastructure/Utilities/dayjs.type';
import { DatePickerRangeValue } from '@forms/Components/DateRangePicker';

import styles from '../RenderCell.module.css';

const defaultSortModel: GridSortModel = [
	{
		field: 'dateCreated',
		sort: 'desc'
	}
];

const defaultEdiStatus: keyof typeof EdiStatus = 'Imported';
const getDefaultDateRange = (): DatePickerRangeValue => [dayjsNow().add(-1, 'week'), dayjsNow()];

export default function EdiLoadToaderDataGrid() {
	const { number: terminalNumber } = useCurrentTerminal();
	const [ediStatus, setEdiStatus] = useState<keyof typeof EdiStatus>(defaultEdiStatus);
	const [dateRangeFilter, setDateRangeFilter] = useState<DatePickerRangeValue>(getDefaultDateRange());

	const { data, isFetching } = useFetchLoadTendersQuery(
		{
			terminalNumber,
			status: ediStatus !== 'All' ? ediStatus : undefined,
			modifyDateFrom: dateRangeFilter[0]?.format('YYYY-MM-DD'),
			modifyDateTo: dateRangeFilter[1]?.format('YYYY-MM-DD')
		},
		{
			skip: !dateRangeFilter[0]?.isValid() || !dateRangeFilter[1]?.isValid()
		}
	);
	const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

	/** We want to use a controlled state for the selection model to allow the toolbar to see row selection changes */
	const [rowSelectionModel, setRowSelectionModel] = useState(emptyRowSet);

	return (
		<EdiLoadTendersContextProvider
			ctxValue={{
				ediStatus: ediStatus,
				setEdiStatus: setEdiStatus,
				modifyDateFrom: dateRangeFilter[0],
				modifyDateTo: dateRangeFilter[1],
				setDateRange: setDateRangeFilter
			}}
		>
			<DataGridPro
				key='edi-load-tender-data-grid'
				rows={data ?? []}
				rowHeight={50}
				columnHeaderHeight={50}
				columns={[
					{
						...GRID_CHECKBOX_SELECTION_COL_DEF,
						renderCell: EdiRowSelectCheckbox,
						width: 60,
						resizable: false
					},
					...ediLoadTenderColumns
				]}
				getRowClassName={() => styles['custom-row']}
				checkboxSelection
				rowSelectionModel={rowSelectionModel}
				onRowSelectionModelChange={(model) => {
					setRowSelectionModel(model);
				}}
				sortModel={defaultSortModel}
				loading={isFetching}
				slots={{
					loadingOverlay: BrandLinearProgress,
					noRowsOverlay: () => <GenericOverlay overlayText='No EDI Load Tenders Found.' />,
					noResultsOverlay: () => <GenericOverlay overlayText='No EDI Load Tender Results.' />,
					toolbar: EdiGridToolbar
				}}
				sx={{
					...applyThemeGridSxOverrides(),
					'& .MuiDataGrid-columnHeaderTitleContainer': {
						justifyContent: 'space-between'
					},
					'& .MuiDataGrid-row.Mui-selected': {
						position: 'relative',
						backgroundColor: 'transparent'
					},
					'& .MuiDataGrid-row.Mui-selected:after': {
						content: '""',
						position: 'absolute',
						width: '100%',
						height: '100%',
						borderLeft: '4px solid var(--mui-palette-primary-main)',
						boxShadow: '0 0 1px 0 rgba(0, 0, 0, 0.5)',
						pointerEvents: 'none',
						zIndex: 5
					},
					'& .MuiDataGrid-row.Mui-selected .row-menu-btn': {
						visibility: 'visible'
					},
					'& .MuiDataGrid-row:hover .row-menu-btn': {
						visibility: 'visible'
					}
				}}
				columnVisibilityModel={{
					rowActions: !isMobile
				}}
				disableRowSelectionOnClick
				className={styles['custom-data-grid']}
			/>
		</EdiLoadTendersContextProvider>
	);
}
